<script lang="ts" setup>
import { onMounted, ref } from "vue";
import { addIcons } from "oh-vue-icons";
import { CoBluetoothB } from "oh-vue-icons/icons";
import { useRouter } from "vue-router";
import { useBleDeviceId } from "@/features/ble/utils/useBleDeviceId.ts";
import { disconnectDevice } from "@/features/ble/utils/disconnectDevice.ts";
import {
  storeBlePool,
  useStoredBlePool,
} from "@/features/ble/utils/localStorage.ts";
import ForgetPoolModal from "@/features/ble/modals/ForgetPoolModal.vue";
import { Dialog } from "@capacitor/dialog";
import { useApi } from "@/services/useApi.ts";
import PoolWifiModalContent from "@/features/pool/header/PoolWifiModalContent.vue";
import { GetWifiStatusResponse } from "@/models/api.models.ts";
import KlereoIcon from "@/shared/components/KlereoIcon.vue";
import BleSignalIcon from "@/features/ble/scan/BleSignalIcon.vue";
import { useI18n } from "vue-i18n";
import PoolParamsWifiAlert from "@/features/pool/header/PoolParamsWifiAlert.vue";
import { useIntervalFn } from "@vueuse/core";

addIcons(CoBluetoothB);

const { t } = useI18n();
const router = useRouter();
const opened = ref(false);

const disconnect = async () => {
  await storeBlePool(deviceId.value, undefined, { autoConnect: false });
  await disconnectDevice(deviceId.value);
  router.push("/ble");
  opened.value = false;
};

const forgetModalOpened = ref(false);
const wifiModalOpened = ref(false);

const deviceId = useBleDeviceId();
const pool = useStoredBlePool(deviceId);

const renamePool = async () => {
  const newName = (
    await Dialog.prompt({
      message: "Choisissez un nom pour ce ce bassin",
      inputText: pool.value?.poolNickname,
    })
  ).value;

  await storeBlePool(pool.value!.deviceId, {
    localNickname: newName || undefined,
  });
};

const wifiStatus = ref<null | GetWifiStatusResponse["response"]>(null);
const rssiStatus = ref<null | any>(null);
const api = useApi();

const refreshWifi = async () => {
  wifiStatus.value = await api.getWifiStatus();
  rssiStatus.value = await api.getRssi();
};

onMounted(refreshWifi);
useIntervalFn(refreshWifi, 5000);

const disconnectWifi = async () => {
  if (
    (
      await Dialog.confirm({
        message: t("WifiDisconnectConfirmationText"),
        okButtonTitle: t("Valid"),
        cancelButtonTitle: t("Abort"),
      })
    ).value
  ) {
    await api.clearWifi();
    await refreshWifi();
  }
};
</script>

<template>
  <ForgetPoolModal
    :pool="pool"
    :opened="forgetModalOpened"
    @cancel="forgetModalOpened = false"
    @submit="
      forgetModalOpened = false;
      router.push('/ble');
    "
  />

  <div v-if="wifiModalOpened">
    <PoolWifiModalContent
      :pool="pool!"
      @close="
        wifiModalOpened = false;
        refreshWifi();
      "
    />
  </div>

  <div v-else class="flex flex-col gap-5 items-stretch">
    <div class="flex gap-1 justify-center">
      <h3>
        {{ pool?.localNickname ?? pool?.poolNickname }}
      </h3>
      <klereo-icon
        class="cursor-pointer"
        name="edit"
        @click="renamePool"
        style="width: 15px"
      />
    </div>

    <hr />

    <div class="text-center">
      {{ $t("BleSectionTitle") }}
    </div>

    <div class="flex gap-2">
      <div class="btn--primary grow" @click="disconnect">
        {{ $t("BleDisconnectAction") }}
      </div>

      <div class="btn--primary grow" @click="forgetModalOpened = true">
        {{ $t("BleForgetAction") }}
      </div>
    </div>

    <hr />

    <div class="text-center">
      {{ $t("WifiSectionTitle") }}
    </div>

    <div class="flex gap-1 justify-between">
      <div class="text-ellipsis overflow-hidden">
        {{
          wifiStatus?.conState === 0
            ? wifiStatus?.ssid
            : $t("WifiDisconnectedStatus")
        }}
      </div>

      <BleSignalIcon
        v-if="wifiStatus?.conState === 0 && rssiStatus?.[0]?.RSSI"
        :rssi="rssiStatus?.[0]?.RSSI"
        :scale="1.2"
      />
    </div>

    <PoolParamsWifiAlert :connected="wifiStatus?.conState === 0" />

    <div class="flex gap-2">
      <div
        class="btn--primary grow"
        @click="disconnectWifi"
        v-if="wifiStatus?.conState === 0"
      >
        {{ $t("WifiDisconnectAction") }}
      </div>

      <div class="btn--primary grow" @click="wifiModalOpened = true" v-else>
        {{ $t("WifiConfigureAction") }}
      </div>
    </div>
  </div>
</template>
